<template>
  <div>
    <v-menu
      offset-overflow
      v-bind:attach="attach"
      v-bind:position-x="positionX"
      v-bind:position-y="positionY"
      v-bind:value="value"
      v-on:input="onInput"
    >
      <v-list
        class="menu-status"
        v-bind:class="{
          line: isCopyAppointmentAction || isPasteAppointmentAction && isChangeStatusAvailable,
        }"
      >
        <v-list-item
          v-if="isCopyAppointmentAction"
          v-on:click="emitCopy">
          <v-list-item-title class="menu-status__item copy">Копировать</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="isPasteAppointmentAction"
          v-bind:disabled="disabledPasteAppointmentAction"
          v-on:click="emitPaste">
          <v-list-item-title class="menu-status__item insert">Вставить</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="isGoToPatientMedicalCard"
          v-on:click="emitTransfer"
        >
          <v-list-item-title class="menu-status__item map line">Перейти в карту</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="isPrintFutureAppointments && !isMobile"
          v-on:click="emitPrintFutureAppointments"
        >
          <v-list-item-title class="menu-status__item print line">Печать приемов</v-list-item-title>
        </v-list-item>
        <template v-if="isChangeStatusAvailable">
          <v-list-item
            v-on:click="emitChangeStatus($options.statuses.noconfirm)">
            <v-list-item-title
              class="menu-status__item unconfirmed line"
              v-bind:class="{ 'is-active': currentStatus === $options.statuses.noconfirm }">
              Не подтвержден
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            v-on:click="emitChangeStatus($options.statuses.confirm)">
            <v-list-item-title
              class="menu-status__item confirmed line"
              v-bind:class="{ 'is-active': currentStatus === $options.statuses.confirm }">
              Подтвержден
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            v-on:click="emitChangeStatus($options.statuses.came)">
            <v-list-item-title
              class="menu-status__item came line"
              v-bind:class="{ 'is-active': currentStatus === $options.statuses.came }">
              Пациент пришел
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            v-on:click="openCancelModalDialog">
            <v-list-item-title class="menu-status__item canceled line">Отменен</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-on:click="emitChangeStatus($options.statuses.absent)">
            <v-list-item-title class="menu-status__item absence line">Неявка</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>
    <v-dialog
      v-model="isShowCancelAppointmentDialog"
      persistent
      content-class="appointment-creation__popup"
      max-width="500px"
    >
      <v-card class="px-5">
        <v-btn
          class="appointment-creation__popup__exit"
          icon
          v-on:click="onCloseCancelAppointmentDialog"
        >
          <v-icon color="#192a3e">mdi-close</v-icon>
        </v-btn>
        <v-card-title class="text-h7 justify-center">
          Отменить визит?
        </v-card-title>
        <autocomplete-input
          attach
          class="appointment-creation__patient"
          item-text="name"
          item-value="id"
          label="Причина отказа"
          placeholder="Выберите причину"
          v-bind:can-delete-items="true"
          v-bind:violations="rejectionReasonInputErrors"
          v-bind:items="[...rejectionReasonList, { name: 'Свой вариант', id: 'new' }]"
          v-model="rejectionReasonId"
          v-on:delete-item="onDeleteRejectionReason"
        />
        <input-text
          v-if="needsCreatingRejectionReason"
          class="appointment-creation__reason-refusal"
          v-bind:required="needsCreatingRejectionReason"
          title="Причина отказа"
          v-model="customRejectionReason"
          v-letter-capital
          v-bind:violations="customRejectionReasonInputErrors" />
        <textarea-length-limitation
          title="Укажите причину отмены визита"
          v-bind:max-length="100"
          rows="3"
          v-letter-capital
          v-bind:violations="appointmentCancelCauseInputErrors"
          v-model="appointmentCancelCause" />
        <v-card-actions class="appointment-creation__popup__button-wrapper justify-center">
          <v-btn
            class="appointment-creation__popup__button-cancel"
            color="blue darken-1"
            text
            v-on:click="onCloseCancelAppointmentDialog">Нет</v-btn>
          <v-btn
            class="appointment-creation__popup__button-accept"
            text
            v-on:click="cancelAppointment">Да, отменить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import mobileMixin from '@/mixins/mobileMixin';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import InputText from '@/components/InputText.vue';

/**
 * @description Название события изменения статуса записи на прием.
 * @type {string}
 */
const NAME_EVENT_CHANGE_APPOINTMENT_STATUS = 'change-appointment-status';

/**
 * @description Название события копирования.
 * @type {string}
 */
const NAME_EVENT_COPY = 'copy';

/**
 * @description Название события удаления причины отказа.
 * @type {string}
 */
const NAME_EVENT_DELETE_REJECTION_REASON = 'delete-rejection-reason';

/**
 * @description Название события ввода.
 * @type {string}
 */
const NAME_EVENT_INPUT = 'input';

/**
 * @description Название события вставки.
 * @type {string}
 */
const NAME_EVENT_PASTE = 'paste';

/**
 * @description Название события перехода к медицинской карте пациента.
 * @type {string}
 */
const NAME_EVENT_TRANSFER_TO_MEDCARD = 'transfer-to-medcard';

/**
 * @description Название события печати будущих записей пациента.
 * @type {string}
 */
const NAME_EVENT_PRINT_FUTURE_APPOINTMENTS = 'print-future-appointments';
const NAME_EVENT_SHOW_CANCEL_DIALOG = 'show-cancel-dialog';

/**
 * @description Допустимые статусы записей
 * @type {{cancel: string, confirm: string, noconfirm: string, came: string}}
 */
const statuses = {
  absent: 'absent',
  came: 'came',
  cancel: 'cancel',
  confirm: 'confirm',
  noconfirm: 'noconfirm',
};

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Компонент контекстного меню слота расписания.
 */
export default {
  components: { InputText, AutocompleteInput, TextareaLengthLimitation },
  computed: {
    rejectionReasonInputErrors: resolveInputErrors('rejection-reason'),
    customRejectionReasonInputErrors: resolveInputErrors('custom-rejection-reason'),
    appointmentCancelCauseInputErrors: resolveInputErrors('cause'),
    needsCreatingRejectionReason() {
      return this.rejectionReasonId === 'new';
    },
  },
  data() {
    return {
      isShowCancelAppointmentDialog: false,
      appointmentCancelCause: '',
      rejectionReasonId: null,
      customRejectionReason: '',
    };
  },
  methods: {
    /**
     * Обработать событие закрытие окна отмены записи.
     * @returns {void}
     */
    onCloseCancelAppointmentDialog() {
      this.appointmentCancelCause = '';
      this.isShowCancelAppointmentDialog = false;
      this.cause = '';
      this.rejectionReasonId = null;
    },
    /**
     * @description Обработать удаление причины отказа.
     * @param {number} id
     */
    onDeleteRejectionReason(id) {
      this.$emit(NAME_EVENT_DELETE_REJECTION_REASON, id);
    },
    /**
     * @description Обнулить переменные для компонента
     *
     * @returns {void}
     */
    refreshVariables() {
      this.appointmentCancelCause = '';
      this.cause = '';
      this.rejectionReasonId = null;
    },
    onInput(isShown) {
      this.$emit(NAME_EVENT_INPUT, isShown);
    },
    emitPaste() {
      this.$emit(NAME_EVENT_PASTE);
    },
    emitCopy() {
      this.$emit(NAME_EVENT_COPY);
    },
    emitChangeStatus(status) {
      this.$emit(NAME_EVENT_CHANGE_APPOINTMENT_STATUS, status, this.appointmentCancelCause);
    },
    /**
     * @description Отправить событие печати будущих записей пациента
     */
    emitPrintFutureAppointments() {
      this.$emit(NAME_EVENT_PRINT_FUTURE_APPOINTMENTS);
    },
    /**
     * @description Отправить событие перехода на страницу мед. карты пациента
     */
    emitTransfer() {
      this.$emit(NAME_EVENT_TRANSFER_TO_MEDCARD);
    },
    /**
     * @description Открыть модальный диалог по отмене записи
     */
    openCancelModalDialog() {
      this.refreshVariables();
      this.$emit(NAME_EVENT_SHOW_CANCEL_DIALOG);
      this.isShowCancelAppointmentDialog = true;
    },
    /**
     * @description Отменить запись на прием
     */
    cancelAppointment() {
      this.$emit(
        NAME_EVENT_CHANGE_APPOINTMENT_STATUS,
        this.$options.statuses.cancel,
        this.appointmentCancelCause,
        this.rejectionReasonId,
        this.customRejectionReason,
      );
    },
  },
  statuses,
  name: 'ContextMenuAppointmentSlot',
  props: {
    /**
     * @description Текущий статус записи.
     * @type {string}
     */
    currentStatus: { default: '', type: String },
    /**
     * @description Список причин отказа.
     */
    rejectionReasonList: { type: Array, required: true },
    /**
     * @description К какому элементу прикреплять.
     * @type {Element|boolean}
     */
    attach: { default: () => false, type: [Element, Boolean] },
    /**
     * @description Есть ли возможность копировать копирование.
     * @type {boolean}
     */
    isCopyAppointmentAction: { default: true, type: Boolean },
    /**
     * @description Есть ли возможность вставить копирование.
     * @type {boolean}
     */
    isPasteAppointmentAction: { default: true, type: Boolean },
    /**
     * @description Деактивировать вставку.
     * @type {boolean}
     */
    disabledPasteAppointmentAction: { default: true, type: Boolean },
    /**
     * @description Есть возможность менять статус записи
     * @type {boolean}
     */
    isChangeStatusAvailable: { default: true, type: Boolean },
    /**
     * @description Есть возможность перехода в мед. карту пациента
     * @type {boolean}
     */
    isGoToPatientMedicalCard: { default: false, type: Boolean },
    /**
     * @description Есть возможность печати новых записей пациента
     * @type {boolean}
     */
    isPrintFutureAppointments: { default: false, type: Boolean },
    /**
     * @description Позиция по оси x.
     * @type {number|null}
     */
    positionX: { default: null, type: Number },
    /**
     * @description Позиция по оси y.
     * @type {number|null}
     */
    positionY: { default: null, type: Number },
    /**
     * @description Показывать меню.
     * @type {boolean}
     */
    value: { required: true, type: Boolean },
    isShowCancelModal: { default: false, type: Boolean },
    inputErrors: { default: () => {}, type: Object },
  },
  mixins: [mobileMixin],
  watch: {
    isShowCancelModal(value) {
      this.isShowCancelAppointmentDialog = value;
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

::v-deep .v-dialog {
  overflow-y: visible;
  .appointment-creation {
    &__patient {
      margin-bottom: 20px;
    }
    &__reason-refusal {
      margin-bottom: 20px;
    }
    &__popup {
      &__button-wrapper {
        padding-block: 10px 16px;
      }
      &__button-cancel.v-btn {
        @include buttons.btn-transparent(10rem, 3rem, 1rem);
        font-weight: 400;
        letter-spacing: normal;
      }
      &__button-accept.v-btn {
        @include buttons.btn-blue(10rem, 3rem, 1rem);
        font-weight: 400;
        letter-spacing: normal;
      }
    }
  }
}

.menu-status {
  min-width: 186px;
  &.line {
    .menu-status__item.line {
      border-top: 1px solid rgba(194, 207, 224, 1);
      margin: 0 -16px;
      padding: 16px 16px;
    }
  }
  div {
    cursor: pointer;
  }
  &__line {
    border-top: 1px solid rgba(194, 207, 224, 1);
  }
  &__item {
    display: flex;
    align-items: center;
    flex-direction: row;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: #323C47;
    cursor: pointer;
    &:before {
      content: '';
      margin: 0 11px 0 0;
      height: 18px;
      width: 18px;
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;
    }
    &.copy:before {
      background-image: url("@/assets/img/status/copy-grey.svg");
    }
    &.insert:before {
      background-image: url("@/assets/img/status/insert-grey.svg");
    }
    &.map:before {
      background-image: url("@/assets/img/status/map-grey.svg");
    }
    &.unconfirmed:before {
      background-color: #abb1a3;
      border-radius: 50%;
      content: "";
      height: 6px;
      width: 6px;
      margin: 0 17px 0 6px;
    }
    &.confirmed:before {
      background-image: url("@/assets/img/status/cheked-grey.svg");
    }
    &.came:before {
      background-image: url("@/assets/img/status/all-checked-grey.svg");
      margin: 0 7px 0 0;
      height: 22px;
      width: 22px;
    }
    &.canceled:before {
      background-image: url("@/assets/img/status/exit-grey.svg");
      margin: 0 7px 0 0;
      height: 22px;
      width: 22px;
    }
    &.absence:before {
      background-image: url("@/assets/img/status/absence-grey.svg");
      margin: 0 7px 0 0;
      height: 22px;
      width: 22px;
    }
    &.print:before {
      background-image: url("@/assets/img/status/print-grey.svg");
      margin: 0 9px 0 0;
      height: 20px;
      width: 20px;
    }
    &:hover,
    &.is-active {
      &.print:before {
        background-image: url("@/assets/img/status/print-blue.svg");
      }
      &.copy:before {
        background-image: url("@/assets/img/status/copy-green.svg");
      }
      &.insert:before {
        background-image: url("@/assets/img/status/incert-green.svg");
      }
      &.unconfirmed:before {
        background-color: #EF474C;
      }
      &.map:before {
        background-image: url("@/assets/img/status/map-blue.svg");
      }
      &.confirmed:before {
        background-image: url("@/assets/img/status/cheked-green.svg");
      }
      &.came:before {
        background-image: url("@/assets/img/status/all-checked-green.svg");
      }
      &.canceled:before {
        background-image: url("@/assets/img/status/exit-red.svg");
      }
      &.absence:before {
        background-image: url("@/assets/img/status/absence-red.svg");
      }
    }
  }
}
</style>
